import React, { useState, useEffect } from 'react';
import { apiFetch, useToast } from '../../App';
import { Plus, Edit2, X, Check, Trash2 } from 'lucide-react';

export default function DenServices() {
  const addToast = useToast();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', species: 'DOG', sizeCategory: '', baseDurationMinutes: 60, basePrice: 1000, detailedDescription: '', whatsIncluded: [], addOns: [], precautions: '', bestFor: '', productsUsed: [] });
  const [includeInput, setIncludeInput] = useState('');
  const [addonName, setAddonName] = useState('');
  const [addonPrice, setAddonPrice] = useState('');
  const [productInput, setProductInput] = useState('');

  useEffect(() => {
    apiFetch('/api/services').then(s => { setServices(s); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    try {
      const payload = { ...form, sizeCategory: form.sizeCategory || null };
      if (editing) {
        const updated = await apiFetch(`/api/services/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        setServices(services.map(s => s.id === editing.id ? { ...s, ...updated } : s));
        addToast('Service updated!');
      } else {
        const newSvc = await apiFetch('/api/services', { method: 'POST', body: JSON.stringify(payload) });
        setServices([...services, newSvc]);
        addToast('Service created!');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', description: '', species: 'DOG', sizeCategory: '', baseDurationMinutes: 60, basePrice: 1000 });
    } catch (e) { addToast(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/api/services/${id}`, { method: 'DELETE' });
      setServices(services.filter(s => s.id !== id));
      addToast('Service deactivated');
    } catch (e) { addToast(e.message, 'error'); }
  };

  const edit = (svc) => {
    setEditing(svc);
    setForm({
      name: svc.name, description: svc.description || '', species: svc.species,
      sizeCategory: svc.sizeCategory || '', baseDurationMinutes: svc.baseDurationMinutes, basePrice: svc.basePrice,
      detailedDescription: svc.detailedDescription || '', whatsIncluded: svc.whatsIncluded || [],
      addOns: svc.addOns || [], precautions: svc.precautions || '',
      bestFor: svc.bestFor || '', productsUsed: svc.productsUsed || [],
    });
    setShowForm(true);
  };

  return (
    <div data-testid="den-services">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Services Catalog</h1>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', description: '', species: 'DOG', sizeCategory: '', baseDurationMinutes: 60, basePrice: 1000 }); }} className="btn-primary text-sm" data-testid="add-service-btn">
          <Plus size={16} /> Add Service
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-3">
          {services.map(svc => (
            <div key={svc.id} className="petfolk-card p-4 flex items-center gap-4" data-testid={`den-service-${svc.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-outfit font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{svc.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--border-color)', color: 'var(--text-secondary)' }}>{svc.species}</span>
                  {svc.sizeCategory && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--border-color)', color: 'var(--text-secondary)' }}>{svc.sizeCategory}</span>}
                </div>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{svc.description}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{svc.baseDurationMinutes} min</p>
              </div>
              <span className="font-outfit text-lg font-bold" style={{ color: 'var(--text-primary)' }}>₹{svc.basePrice?.toLocaleString()}</span>
              <div className="flex gap-1">
                <button onClick={() => edit(svc)} className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5" data-testid={`edit-service-${svc.id}`}>
                  <Edit2 size={16} style={{ color: 'var(--text-secondary)' }} />
                </button>
                <button onClick={() => handleDelete(svc.id)} className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`delete-service-${svc.id}`}>
                  <Trash2 size={16} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="petfolk-card w-full max-w-lg mx-4 p-6 max-h-[85vh] overflow-auto animate-slide-up" onClick={e => e.stopPropagation()} data-testid="service-form-modal">
            <div className="flex justify-between mb-4">
              <h2 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{editing ? 'Edit Service' : 'Add Service'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <input className="petfolk-input" placeholder="Service name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="service-name-input" />
              <textarea className="petfolk-input" placeholder="Short description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} />
              <div className="flex gap-2">
                {['DOG', 'CAT'].map(s => (
                  <button key={s} onClick={() => setForm({...form, species: s})} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold ${form.species === s ? 'bg-petfolk-orange text-white' : ''}`} style={{ border: '1px solid var(--border-color)', color: form.species !== s ? 'var(--text-secondary)' : undefined }}>
                    {s}
                  </button>
                ))}
              </div>
              <select className="petfolk-input" value={form.sizeCategory} onChange={e => setForm({...form, sizeCategory: e.target.value})} data-testid="service-size-select">
                <option value="">All Sizes</option>
                <option value="SMALL">Small (&lt;10kg)</option>
                <option value="MEDIUM">Medium (10-25kg)</option>
                <option value="LARGE">Large (&gt;25kg)</option>
              </select>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Duration (min)</label>
                  <input className="petfolk-input" type="number" value={form.baseDurationMinutes} onChange={e => setForm({...form, baseDurationMinutes: parseInt(e.target.value) || 0})} data-testid="service-duration-input" />
                </div>
                <div>
                  <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Base Price (₹)</label>
                  <input className="petfolk-input" type="number" value={form.basePrice} onChange={e => setForm({...form, basePrice: parseFloat(e.target.value) || 0})} data-testid="service-price-input" />
                </div>
              </div>

              {/* Expanded Detail Fields */}
              <div className="pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                <h3 className="font-outfit font-semibold text-sm mb-3" style={{ color: 'var(--text-primary)' }}>Service Details (shown when expanded)</h3>
              </div>

              <div>
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Detailed Description</label>
                <textarea className="petfolk-input" placeholder="In-depth service description..." value={form.detailedDescription} onChange={e => setForm({...form, detailedDescription: e.target.value})} rows={3} data-testid="service-detailed-desc" />
              </div>

              <div>
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Best For (breeds/types)</label>
                <input className="petfolk-input" placeholder="e.g., Golden Retriever, Labrador, Husky" value={form.bestFor} onChange={e => setForm({...form, bestFor: e.target.value})} data-testid="service-best-for" />
              </div>

              <div>
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Precautions / Good to Know</label>
                <input className="petfolk-input" placeholder="Any warnings or notes" value={form.precautions} onChange={e => setForm({...form, precautions: e.target.value})} data-testid="service-precautions" />
              </div>

              {/* What's Included */}
              <div>
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>What's Included</label>
                <div className="flex gap-2 mb-2">
                  <input className="petfolk-input flex-1" placeholder="e.g., Premium shampoo bath" value={includeInput} onChange={e => setIncludeInput(e.target.value)} data-testid="include-input" />
                  <button onClick={() => { if (includeInput.trim()) { setForm({...form, whatsIncluded: [...form.whatsIncluded, includeInput.trim()]}); setIncludeInput(''); } }} className="btn-primary px-3 text-sm" data-testid="add-include-btn">+</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.whatsIncluded.map((item, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(34,197,94,0.1)', color: '#16A34A' }}>
                      {item}
                      <button onClick={() => setForm({...form, whatsIncluded: form.whatsIncluded.filter((_, j) => j !== i)})} className="hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Add-Ons */}
              <div>
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Add-Ons</label>
                <div className="flex gap-2 mb-2">
                  <input className="petfolk-input flex-1" placeholder="Add-on name" value={addonName} onChange={e => setAddonName(e.target.value)} data-testid="addon-name-input" />
                  <input className="petfolk-input w-24" type="number" placeholder="₹ Price" value={addonPrice} onChange={e => setAddonPrice(e.target.value)} data-testid="addon-price-input" />
                  <button onClick={() => { if (addonName.trim() && addonPrice) { setForm({...form, addOns: [...form.addOns, { name: addonName.trim(), price: parseFloat(addonPrice) }]}); setAddonName(''); setAddonPrice(''); } }} className="btn-secondary px-3 text-sm" data-testid="add-addon-btn">+</button>
                </div>
                <div className="space-y-1">
                  {form.addOns.map((a, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(139,92,246,0.05)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{a.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>₹{a.price}</span>
                        <button onClick={() => setForm({...form, addOns: form.addOns.filter((_, j) => j !== i)})} className="text-red-400 hover:text-red-600">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Products Used */}
              <div>
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Products Used</label>
                <div className="flex gap-2 mb-2">
                  <input className="petfolk-input flex-1" placeholder="e.g., Organic oatmeal shampoo" value={productInput} onChange={e => setProductInput(e.target.value)} data-testid="product-input" />
                  <button onClick={() => { if (productInput.trim()) { setForm({...form, productsUsed: [...form.productsUsed, productInput.trim()]}); setProductInput(''); } }} className="btn-primary px-3 text-sm">+</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.productsUsed.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(32,178,170,0.1)', color: '#0D9488' }}>
                      {p}
                      <button onClick={() => setForm({...form, productsUsed: form.productsUsed.filter((_, j) => j !== i)})} className="hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
              </div>

              <button onClick={handleSave} className="btn-primary w-full justify-center" data-testid="save-service-btn">
                <Check size={16} /> {editing ? 'Update' : 'Create'} Service
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
